import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router";
import { useSearchParams } from "react-router-dom";
import { AddOnItemName, SearchParams } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import AuthForm from "components/Auth";
import { PAGE_VISITED } from "constants/trackingProps";
import { AuthAction } from "features/Auth/schema";
import { fetcherAuth } from "utils/api";
import { getParams, getSource, identifyUser, trackEvent } from "utils/tracking";

import { getRedirectUrl } from "../helpers";
import ProgramInfo from "./ProgramInfo";

interface Props {
  fromRoutes: string;
  checkOutForSplashPage: (plan: any) => void;
  planDetails: JSX.Element;
  sidebarPic: string;
  plan: AddOnItemName | Plans;
  notQualified: boolean;
  buttonText?: JSX.Element;
  buttonTextBefore?: JSX.Element;
  buttonLabel?: string;
  additionalRoutes?: JSX.Element;
}

const SplashPageHome = ({
  fromRoutes,
  checkOutForSplashPage,
  planDetails,
  sidebarPic,
  plan,
  notQualified,
  buttonText,
  buttonTextBefore,
  buttonLabel,
  additionalRoutes,
}: Props) => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const isSignedIn = getParams("signed_in") === "true";
  const source = getSource();

  const { getToken, isLoaded } = useAuth();
  const [fetchUserLoading, setFetchUserLoading] = useState(false);
  const redirectUrl = getRedirectUrl(fromRoutes, location);
  const affiliateCode = searchParams.get(SearchParams.AFFILIATE_CODE);

  const { currentOrg, setCurrentOrg, setCurrentUser, setProfile } = useContext(
    OrganizationUserContext,
  );
  const { setSubscription } = useContext(SubscriptionContext);
  const fetchUserInfo = async () => {
    try {
      setFetchUserLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/user?affiliate_code=${affiliateCode || ""}`,
      );
      identifyUser(res.organizationUser.id);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
      setProfile(res.organizationProfile);
    } catch (error) {
      handleClerkUserSignUp();
    } finally {
      setFetchUserLoading(false);
    }
  };

  const handleClerkUserSignUp = async () => {
    setFetchUserLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/signup`,
        "POST",
        {},
        {
          source,
          affiliateCode,
        },
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      if (
        // @ts-ignore
        typeof window.fbq !== "undefined" &&
        // @ts-ignore
        typeof window.fbq === "function"
      ) {
        // @ts-ignore
        window.fbq("track", "CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.ttq !== "undefined" &&
        // @ts-ignore
        typeof window.ttq.track === "function"
      ) {
        // @ts-ignore
        window.ttq.track("CompleteRegistration");
      }
      setFetchUserLoading(false);
    } catch (error) {
      setFetchUserLoading(false);
    }
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (!currentOrg?.id) {
      fetchUserInfo();
    } else if (isSignedIn && plan) {
      checkOutForSplashPage(plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentOrg?.id]);

  return fetchUserLoading || !isLoaded ? (
    <Backdrop
      sx={{
        backgroundColor: "white",
        zIndex: 9999,
        position: "absolute",
      }}
      open={fetchUserLoading}
      appear={false}
    >
      <CircularProgress />
    </Backdrop>
  ) : (
    <Grid
      container
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: "auto",
        justifyContent: "center",
        backgroundColor: "yellow.light",
      }}
    >
      <Grid container>
        {!isMobileScreen && (
          <Grid
            item
            xs={4}
            sx={{
              backgroundImage: `url(${sidebarPic})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          ></Grid>
        )}

        <Grid
          item
          xs
          md
          lg
          container
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Routes>
            <Route
              path="/"
              element={
                <ProgramInfo
                  checkoutForSplashPage={checkOutForSplashPage}
                  notQualified={notQualified}
                  planDetails={planDetails}
                  fromRoutes={fromRoutes}
                  fetchUserLoading={fetchUserLoading}
                  plan={plan}
                  buttonTextBefore={buttonTextBefore}
                  buttonText={buttonText}
                  buttonLabel={buttonLabel}
                />
              }
            />
            <Route
              path="/sign-in/*"
              element={
                <AuthForm
                  redirectUrl={redirectUrl}
                  action={AuthAction.SIGN_IN}
                  signInUrl={`/${fromRoutes}/sign-in${location.search}`}
                  signUpUrl={`/${fromRoutes}/sign-up${location.search}`}
                />
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <AuthForm
                  redirectUrl={redirectUrl}
                  action={AuthAction.SIGN_UP}
                  signInUrl={`/${fromRoutes}/sign-in${location.search}`}
                  signUpUrl={`/${fromRoutes}/sign-up${location.search}`}
                />
              }
            />
            {additionalRoutes}
          </Routes>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SplashPageHome;
